import { perfilActualizarSchema, type PerfilActualizarInput } from '@app/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { Alert } from '../../../components/ui/alert';
import { Avatar } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorCode, getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { useCurrentUser } from '../../auth/hooks/useCurrentUser';
import { perfilApi, resolveAvatarSrc } from '../api/perfil.api';

const AVATAR_MIME_CLIENTE = ['image/jpeg', 'image/png', 'image/webp'];
const AVATAR_MAX_BYTES = 2 * 1024 * 1024;

type Feedback = { kind: 'success' | 'error'; msg: string } | null;

export function MiPerfilPage(): JSX.Element {
  const { data: user, isLoading } = useCurrentUser();
  const es = useI18n();

  if (isLoading || !user) {
    return <div className="p-6 text-sm text-text-faint">{es.perfil.cargando}</div>;
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-marca-gris">{es.perfil.titulo}</h1>
      <DatosPersonales />
      <CambiarPasswordCard />
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-lg border border-marca-neutro bg-surface p-5 shadow-sm">
      <h2 className="mb-4 text-lg font-medium text-marca-gris">{title}</h2>
      {children}
    </section>
  );
}

function DatosPersonales(): JSX.Element {
  const { data: user } = useCurrentUser();
  const es = useI18n();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);
  // Cache-buster para forzar recarga del <img> tras subir/quitar la foto.
  const [avatarVersion, setAvatarVersion] = useState(() => Date.now());

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<PerfilActualizarInput>({
    resolver: zodResolver(perfilActualizarSchema),
    values: { nombre: user?.nombre ?? '' },
  });

  const updateMutation = useMutation({
    mutationFn: perfilApi.actualizar,
    onSuccess: () => {
      setFeedback({ kind: 'success', msg: es.perfil.datosPersonales.exito });
      void queryClient.invalidateQueries({ queryKey: qk.auth.me });
    },
    onError: (error) =>
      setFeedback({ kind: 'error', msg: getErrorMessage(error, es.errores.generico.algoSalioMal) }),
  });

  const avatarMutation = useMutation({
    mutationFn: (file: File) => perfilApi.subirAvatar(file),
    onSuccess: () => {
      setFeedback({ kind: 'success', msg: es.perfil.avatar.exitoSubida });
      setAvatarVersion(Date.now());
      void queryClient.invalidateQueries({ queryKey: qk.auth.me });
    },
    onError: (error) => {
      const code = getErrorCode(error);
      const msg =
        code === 'AVATAR_MIME_NO_PERMITIDO'
          ? es.perfil.avatar.errorFormato
          : code === 'AVATAR_DEMASIADO_GRANDE'
            ? es.perfil.avatar.errorTamanio
            : getErrorMessage(error, es.errores.generico.algoSalioMal);
      setFeedback({ kind: 'error', msg });
    },
  });

  const quitarMutation = useMutation({
    mutationFn: perfilApi.quitarAvatar,
    onSuccess: () => {
      setFeedback({ kind: 'success', msg: es.perfil.avatar.exitoQuitada });
      setAvatarVersion(Date.now());
      void queryClient.invalidateQueries({ queryKey: qk.auth.me });
    },
    onError: (error) =>
      setFeedback({ kind: 'error', msg: getErrorMessage(error, es.errores.generico.algoSalioMal) }),
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    e.target.value = ''; // permitir re-elegir el mismo archivo
    if (!file) return;
    // Validación cliente (el backend revalida por tamaño + magic bytes).
    if (!AVATAR_MIME_CLIENTE.includes(file.type)) {
      setFeedback({ kind: 'error', msg: es.perfil.avatar.errorFormato });
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      setFeedback({ kind: 'error', msg: es.perfil.avatar.errorTamanio });
      return;
    }
    avatarMutation.mutate(file);
  }

  if (!user) return <Card title={es.perfil.datosPersonales.titulo}>{es.perfil.cargando}</Card>;

  const avatarBusy = avatarMutation.isPending || quitarMutation.isPending;

  return (
    <Card title={es.perfil.datosPersonales.titulo}>
      <p className="mb-4 text-sm text-text-faint">{es.perfil.datosPersonales.descripcion}</p>
      {feedback && (
        <Alert variant={feedback.kind} className="mb-4">
          {feedback.msg}
        </Alert>
      )}

      <div className="flex flex-col gap-6 sm:flex-row">
        {/* Avatar */}
        <div className="flex flex-col items-center gap-3">
          <Avatar
            src={resolveAvatarSrc(user, avatarVersion)}
            nombre={user.nombre}
            size={96}
            alt={es.perfil.avatar.alt}
            className="ring-1 ring-marca-neutro"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept={AVATAR_MIME_CLIENTE.join(',')}
            className="hidden"
            onChange={onPickFile}
          />
          <div className="flex flex-col items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={avatarBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload size={14} />
              {avatarMutation.isPending
                ? es.perfil.avatar.subiendo
                : user.avatarUrl
                  ? es.perfil.avatar.cambiar
                  : es.perfil.avatar.subir}
            </Button>
            {user.avatarUrl && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={avatarBusy}
                onClick={() => quitarMutation.mutate()}
              >
                <Trash2 size={14} />
                {quitarMutation.isPending ? es.perfil.avatar.quitando : es.perfil.avatar.quitar}
              </Button>
            )}
            <span className="text-center text-xs text-text-faint">{es.perfil.avatar.ayuda}</span>
          </div>
        </div>

        {/* Datos */}
        <form
          className="flex flex-1 flex-col gap-4"
          onSubmit={(e) => void handleSubmit((d) => updateMutation.mutate(d))(e)}
          noValidate
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="nombre">{es.perfil.datosPersonales.nombre}</Label>
            <Input id="nombre" aria-invalid={!!errors.nombre} {...register('nombre')} />
            {errors.nombre && <span className="text-xs text-danger">{errors.nombre.message}</span>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ReadonlyField label={es.perfil.datosPersonales.email} value={user.email} />
            <ReadonlyField label={es.perfil.datosPersonales.rol} value={es.roles[user.rol]} />
          </div>

          <div>
            <Button type="submit" disabled={isSubmitting || updateMutation.isPending || !isDirty}>
              {updateMutation.isPending
                ? es.perfil.datosPersonales.guardando
                : es.perfil.datosPersonales.guardar}
            </Button>
          </div>
        </form>
      </div>
    </Card>
  );
}

function ReadonlyField({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-faint">{label}</span>
      <span className="text-sm text-marca-gris">{value}</span>
    </div>
  );
}

function CambiarPasswordCard(): JSX.Element {
  const es = useI18n();
  return (
    <Card title={es.perfil.password.titulo}>
      <p className="mb-4 text-sm text-text-faint">{es.perfil.password.descripcion}</p>
      <Button asChild variant="outline" size="sm">
        <Link to="/cambiar-password">
          <KeyRound size={14} />
          {es.perfil.password.cambiar}
        </Link>
      </Button>
    </Card>
  );
}
